import { ClientConfigurationError } from '@/lib/errors/client';

import {
  CHAIN_ID_PER_NETWORK_URL_PATH,
  NATIVE_TOKEN_SYMBOL_PER_CHAIN,
  NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID,
  SUPPORTED_CHAINS_BY_CHAIN_ID,
} from '@/constants/chain';

import { getBaseURL } from './environment';
import { isChainAddress } from './models';
import { canonicalSolanaMint, isTokenAddressNativeAsset } from './multichain';
import { routes } from './routes';
import { TokenInfo } from './token.types';

export function getValidChainIdFromString(rawChainId: string): number {
  // 1 - Convert the full network name from the url to a valid chainId
  const chainId = CHAIN_ID_PER_NETWORK_URL_PATH[rawChainId] ?? parseInt(rawChainId, 10);

  // If the chainId is not supported, throw an error.
  if (
    typeof chainId !== 'number' ||
    Number.isNaN(chainId) ||
    !SUPPORTED_CHAINS_BY_CHAIN_ID[chainId]
  ) {
    throw new ClientConfigurationError(`Invalid chainId: ${rawChainId}`);
  }

  return chainId;
}

/**
 * Parses token metadata from scalar address and chain query parameters.
 */
export const getTokenInfoFromQueryParameter = (
  rawAddressQueryParam: string | string[] | undefined,
  rawChainIdOrNetworkNameQueryParam: string | string[] | undefined,
): TokenInfo | undefined => {
  const address =
    rawAddressQueryParam &&
    !Array.isArray(rawAddressQueryParam) &&
    isChainAddress(rawAddressQueryParam)
      ? rawAddressQueryParam
      : undefined;
  let chainId: number | undefined;

  try {
    chainId =
      rawChainIdOrNetworkNameQueryParam && !Array.isArray(rawChainIdOrNetworkNameQueryParam)
        ? getValidChainIdFromString(rawChainIdOrNetworkNameQueryParam)
        : undefined;
  } catch {
    // An invalid chain query parameter does not resolve to a token.
  }

  return address && chainId
    ? {
        address,
        chainId,
      }
    : undefined;
};

/**
 * Returns the URL for a token logo.
 * @param address - The address of the token
 * @param chainId - The chain ID of the token
 * @returns The URL for the token logo
 */
export const getTokenLogoURL = (address?: string, chainId?: number) => {
  const defaultLogoURL = getBaseURL() + '/images/liquidity-providers/default.png';
  if (address && chainId) {
    return `https://matcha-image-proxy.api.0x.org/images/${chainId}/${canonicalSolanaMint(address)}`;
  }
  return defaultLogoURL;
};

export type TokenRef = { address: string; chainId: number };

type TradeQuery = { mode?: string };

/**
 * Builds a token profile route, optionally carrying the trade module mode.
 */
export const tokenPageRoute = (token: TokenRef, tradeQuery?: TradeQuery) => {
  const networkSlug = NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[token.chainId] ?? String(token.chainId);

  const contractAddress = isTokenAddressNativeAsset(token.address, token.chainId)
    ? (NATIVE_TOKEN_SYMBOL_PER_CHAIN[token.chainId]?.toLowerCase() ?? token.address)
    : token.address;

  const query: { [key: string]: string | string[] } = {
    chainId: networkSlug,
    contractAddresses: [contractAddress],
  };
  if (tradeQuery?.mode) {
    query.mode = tradeQuery.mode;
  }

  return { pathname: routes.TOKEN_PROFILE, query };
};

/**
 * Builds a homepage route with the token preselected as the buy side.
 */
export const tradeRoute = (token: TokenRef, tradeQuery?: TradeQuery) => {
  const query: { [key: string]: string } = {
    buyChain: token.chainId.toString(),
    buyAddress: token.address,
  };
  if (tradeQuery?.mode) {
    query.mode = tradeQuery.mode;
  }

  return { pathname: routes.INDEX, query };
};

/**
 * Stringifies a route built above, for the meta-click `window.open` sites.
 */
export const tokenRouteHref = (
  route: ReturnType<typeof tokenPageRoute> | ReturnType<typeof tradeRoute>,
) => {
  if (route.pathname === routes.INDEX) {
    return `/?${new URLSearchParams(route.query as Record<string, string>).toString()}`;
  }

  const { chainId, contractAddresses, ...rest } = route.query as {
    chainId: string;
    contractAddresses: string[];
    [key: string]: string | string[];
  };
  const search = new URLSearchParams(rest as Record<string, string>).toString();
  return `/tokens/${chainId}/${contractAddresses[0]}${search ? `?${search}` : ''}`;
};
